/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  PaginationResponse,
  IErrorMessage,
  PageMode,
  SAMLConfigurationResponse,
  defaultSAMLConfigResponse,
  IConstraintViolationMessage,
} from "@advapacs/common";

import { SAMLSPResponse } from "@advapacs/application-api-protos/dist/Authentication";

const SAMLSlice = createSlice({
  name: "SAMLSlice",
  initialState: {
    loading: false,
    entity: defaultSAMLConfigResponse as SAMLConfigurationResponse,
    entityId: null as string | null,
    entities: [] as Array<SAMLConfigurationResponse> | [],
    errorMessage: {} as IErrorMessage | IConstraintViolationMessage | null,
    constrainErrorMessage: null as IConstraintViolationMessage | null,
    pageMode: null as PageMode | null,
    isSaving: false,
    isViewModePrevious: false,
  },
  reducers: {
    setEditMode(state, action: PayloadAction<PageMode | null>) {
      state.pageMode = action.payload;
    },
    setIsSaving(state, action: PayloadAction<boolean>) {
      state.isSaving = action.payload;
    },
    setEntityId(state, action: PayloadAction<string | null>) {
      state.entityId = action.payload;
    },
    clearFormState(state) {
      state.errorMessage = null;
      state.constrainErrorMessage = null;
      state.isSaving = false;
      state.entity = defaultSAMLConfigResponse;
    },
    clearListState(state) {
      state.errorMessage = null;
      state.constrainErrorMessage = null;
      state.isSaving = false;
      state.entities = [];
      state.isViewModePrevious = false;
    },
    getSAMLConfigurationsStart(state) {
      state.loading = true;
      state.pageMode = null;
      state.entities = [];
    },
    getSAMLConfigurationsSuccess(
      state,
      action: PayloadAction<PaginationResponse<SAMLConfigurationResponse>>
    ) {
      state.loading = false;
      state.entities = action.payload.content;
    },
    getSAMLConfigurationsFailed(state, action: PayloadAction<IErrorMessage>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    getSAMLConfigurationStart(state) {
      state.loading = true;
      state.entity = defaultSAMLConfigResponse;
    },
    getSAMLConfigurationSuccess(
      state,
      action: PayloadAction<SAMLConfigurationResponse>
    ) {
      state.loading = false;
      state.entity = action.payload;
    },
    getSAMLConfigurationFailed(state, action: PayloadAction<IErrorMessage>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    createSAMLConfigurationStart(state) {
      state.loading = true;
    },
    createSAMLConfigurationSuccess(state) {
      state.loading = false;
      state.pageMode = PageMode.LIST;
    },
    createSAMLConfigurationFailed(
      state,
      action: PayloadAction<IErrorMessage | IConstraintViolationMessage>
    ) {
      state.loading = false;
      if (action.payload.code === "422") {
        state.constrainErrorMessage =
          action.payload as IConstraintViolationMessage;
      } else {
        state.errorMessage = action.payload;
      }
    },
    updateSAMLConfigurationStart(state) {
      state.loading = true;
    },
    updateSAMLConfigurationSuccess(state) {
      state.loading = false;
      state.pageMode = state.isViewModePrevious ? PageMode.VIEW : PageMode.LIST;
    },
    updateSAMLConfigurationFailed(
      state,
      action: PayloadAction<IErrorMessage | IConstraintViolationMessage>
    ) {
      state.loading = false;
      if (action.payload.code === "422") {
        state.constrainErrorMessage =
          action.payload as IConstraintViolationMessage;
      } else {
        state.errorMessage = action.payload;
      }
    },
    getSpIdStart(state) {
      state.loading = true;
      state.entity = defaultSAMLConfigResponse;
    },
    getSpIdSuccess(state, action: PayloadAction<SAMLSPResponse>) {
      state.loading = false;
      const { id, spEntityId, spAcsUrl } = action.payload;
      state.entity = {
        ...state.entity,
        id,
        spEntityId,
        spAcsUrl,
      };
    },
    getSpIdFailed(state, action: PayloadAction<IErrorMessage>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    deleteSAMLConfigurationStart(state) {
      state.loading = true;
    },
    deleteSAMLConfigurationSuccess(state) {
      state.loading = false;
      state.pageMode = PageMode.LIST;
    },
    deleteSAMLConfigurationFailed(state, action: PayloadAction<IErrorMessage>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    setViewModePrevious(state, action: PayloadAction<boolean>) {
      state.isViewModePrevious = action.payload;
    },
  },
});

export const SAMLAction = SAMLSlice.actions;
export default SAMLSlice.reducer;
